import { useEffect, useState, type ReactNode } from "react";
import QRCode from "qrcode";
import {
  X, Plus, Globe, Lock, ShieldAlert, Eye, Download, Check, Copy, Settings2,
  Mail, QrCode, MessageCircle, FileText, UserPlus, Clock, KeyRound, Stamp, Bell, ShieldCheck,
} from "lucide-react";
import { Modal, Switch, Button, Avatar, Field, Loader, useToast, promptDialog } from "@/shared/ui";
import { apiPost } from "@/features/shared/apiClient";
import { useAuth } from "@/features/auth/context/AuthContext";
import { listMembers, type TeamMember } from "@/features/team/teamApi";
import EmbedSettings from "@/features/embed/EmbedSettings";
import {
  listEmbeds,
  createEmbed,
  updateEmbed,
  updateModel,
  listDomains,
  type EmbedOptions,
  type VaultModel,
  type VaultEmbed,
} from "@/features/vault/vaultApi";

const buildUrl = (base: string, token: string) => `${base}/embed/${token}`;
const buildSnippet = (base: string, token: string) =>
  `<iframe src="${buildUrl(base, token)}" width="100%" height="600" frameborder="0" allow="xr-spatial-tracking; fullscreen" allowfullscreen></iframe>`;

const ROLE_OPTIONS = [
  { value: "view", label: "Can view" },
  { value: "download", label: "Can download" },
  { value: "comment", label: "Can comment" },
  { value: "approve", label: "Can approve" },
];

function normalize(d: string): string {
  return d.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/:\d+$/, "");
}

/** Self-serve share + embed, mirroring the reference Share modal: invite-by-email + role,
 *  share-by-link + WhatsApp, PDF/Email/QR actions, and access ToggleRows — on top of the
 *  real embed/public-link plumbing (public toggle, copyable link + iframe, domain allow-list,
 *  download permission). Controls with a real backend field (public visibility, allow-download,
 *  allowed domains, access level, expiry) persist; the rest are seeded from the embed and kept local. */
export default function ShareModal({ model, onClose }: { model: VaultModel; onClose: () => void }) {
  const { activeWorkspace, user } = useAuth();
  const { toast } = useToast();
  const slug = activeWorkspace?.workspaceSlug;

  const [embed, setEmbed] = useState<VaultEmbed | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [published, setPublished] = useState(model.visibility === "public");
  const [pubBusy, setPubBusy] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [embedOpen, setEmbedOpen] = useState(false);

  const [bases, setBases] = useState<string[]>([window.location.origin]);
  const [base, setBase] = useState(window.location.origin);

  // Reference sections. Access level + expiry persist via updateEmbed's share arg; the rest are local.
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("view");
  const [accessRole, setAccessRole] = useState("view");
  const [expiryOn, setExpiryOn] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [password, setPassword] = useState(false);
  const [watermark, setWatermark] = useState(false);
  const [notify, setNotify] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState("");

  useEffect(() => {
    listDomains()
      .then((d) => {
        const active = d.domains.filter((x) => x.status === "active").map((x) => `https://${x.domain}`);
        if (active.length) {
          setBases([...active, window.location.origin]);
          setBase(active[0]);
        }
      })
      .catch(() => undefined);
    listMembers()
      .then((r) => {
        setMembers(r.members);
        setOwnerId(r.ownerId);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const existing = await listEmbeds(model._id);
        const e = existing[0] ?? (await createEmbed(model._id, [])).embed;
        if (!alive) return;
        setEmbed(e);
        setDomains(e.allowedDomains);
        setAllowDownload(!!e.options?.allowDownload);
        setAccessRole(e.access);
        setExpiryOn(!!e.expiresAt);
        setExpiryDate(e.expiresAt ? e.expiresAt.slice(0, 10) : "");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [model._id]);

  const shareLink = embed ? buildUrl(base, embed.token) : "";

  // Regenerate the QR whenever it's open and the link changes (base swap / token load).
  useEffect(() => {
    if (!qrOpen || !shareLink) return;
    let alive = true;
    QRCode.toDataURL(shareLink, { margin: 1, width: 320 })
      .then((u) => alive && setQrUrl(u))
      .catch(() => alive && setQrUrl(""));
    return () => {
      alive = false;
    };
  }, [qrOpen, shareLink]);

  const togglePublish = async () => {
    setPubBusy(true);
    try {
      const next = !published;
      await updateModel(model._id, { visibility: next ? "public" : "private" });
      setPublished(next);
    } finally {
      setPubBusy(false);
    }
  };

  const toggleDownload = async () => {
    if (!embed) return;
    setDlBusy(true);
    try {
      const next = !allowDownload;
      const options: EmbedOptions = { ...(embed.options ?? {}), allowDownload: next };
      const r = await updateEmbed(embed._id, embed.allowedDomains, options);
      setEmbed(r.embed);
      setAllowDownload(next);
    } finally {
      setDlBusy(false);
    }
  };

  const addDomain = () => {
    const d = normalize(input);
    if (d && !domains.includes(d)) {
      setDomains((ds) => [...ds, d]);
      setDirty(true);
    }
    setInput("");
  };
  const removeDomain = (d: string) => {
    setDomains((ds) => ds.filter((x) => x !== d));
    setDirty(true);
  };
  const saveDomains = async () => {
    if (!embed) return;
    setSaving(true);
    try {
      const r = await updateEmbed(embed._id, domains, embed.options);
      setEmbed(r.embed);
      setDirty(false);
      toast({ tone: "success", title: "Allowed domains saved" });
    } finally {
      setSaving(false);
    }
  };

  // Persist access-level / expiry to the embed (fire on change).
  const persistShare = async (share: { access?: string; expiresAt?: string | null }) => {
    if (!embed) return;
    const r = await updateEmbed(embed._id, embed.allowedDomains, embed.options, share);
    setEmbed(r.embed);
  };
  const changeAccess = (v: string) => {
    setAccessRole(v);
    void persistShare({ access: v });
  };
  const toggleExpiry = () => {
    const next = !expiryOn;
    setExpiryOn(next);
    if (!next) {
      setExpiryDate("");
      void persistShare({ expiresAt: null }); // clearing the toggle clears the expiry
    }
  };
  const changeExpiryDate = (v: string) => {
    setExpiryDate(v);
    void persistShare({ expiresAt: v || null });
  };

  const invite = () => {
    if (!inviteEmail.trim()) return;
    toast({
      tone: "info",
      title: "Email invites aren't wired up yet",
      description: "Share the link below — access is controlled by the toggles.",
    });
    setInviteEmail("");
  };

  const whatsapp = () => {
    if (!shareLink) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareLink)}`, "_blank", "noopener");
  };
  const emailShare = async () => {
    if (!embed) return;
    const to = (await promptDialog({ title: "Email the share link to", label: "Email", defaultValue: inviteEmail || "" }))?.trim();
    if (!to) return;
    try {
      await apiPost("/share/email", { token: embed.token, to });
      toast({ tone: "success", title: `Shared with ${to}` });
    } catch (e) {
      toast({ tone: "error", title: "Couldn't send email", description: e instanceof Error ? e.message : undefined });
    }
  };
  const previewAsClient = () => {
    const url = slug ? `${window.location.origin}/showcase/${slug}` : shareLink;
    if (url) window.open(url, "_blank", "noopener");
  };
  const done = async () => {
    if (dirty) await saveDomains();
    onClose();
  };

  // People with access: owner first, then members (workspace-wide read access).
  const people = members
    .map((m) => {
      const u = typeof m.userId === "object" ? m.userId : null;
      const id = u?._id ?? (typeof m.userId === "string" ? m.userId : "");
      return { id, name: u?.name ?? "Member", email: u?.email ?? "", avatar: u?.avatar, isOwner: id === ownerId };
    })
    .sort((a, b) => Number(b.isOwner) - Number(a.isOwner));

  return (
    <Modal open onClose={onClose} title="Share" description={model.name} size="md">
      {loading || !embed ? (
        <div className="flex h-44 items-center justify-center">
          <Loader label="Preparing share…" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Invite by email + role */}
          <div>
            <SectionLabel>Invite people</SectionLabel>
            <div className="flex items-stretch gap-2">
              <Field
                label="name@client.com"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), invite())}
                className="flex-1"
              />
              <MiniSelect value={inviteRole} onChange={setInviteRole} options={ROLE_OPTIONS} className="w-[132px]" />
              <Button variant="gold" leadingIcon={<UserPlus size={14} />} disabled={!inviteEmail.trim()} onClick={invite}>
                Invite
              </Button>
            </div>
            {people.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {people.map((p) => (
                  <PersonRow key={p.id} name={p.name} email={p.email} avatar={p.avatar} role={p.isOwner ? "Owner" : "Member"} />
                ))}
              </div>
            )}
            {people.length === 0 && user && (
              <div className="mt-3">
                <PersonRow name={user.name} email={user.email} avatar={user.avatar} role="Owner" />
              </div>
            )}
          </div>

          {/* Share by link + WhatsApp */}
          <div>
            <SectionLabel>Or share by link</SectionLabel>
            {bases.length > 1 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {bases.map((b) => (
                  <button
                    key={b}
                    onClick={() => setBase(b)}
                    className={`rounded-full border px-2.5 py-1 text-[11.5px] transition-colors ${
                      base === b
                        ? "border-transparent bg-ink-900 text-white"
                        : "border-white/60 bg-white/40 text-ink-600 hover:bg-white/60"
                    }`}
                  >
                    {b.replace(/^https?:\/\//, "")}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-stretch gap-2">
              <code className="flex-1 truncate rounded-lg border border-white/60 bg-white/40 px-3 py-2 font-mono text-[11.5px] text-ink-700">
                {shareLink}
              </code>
              <CopyBtn value={shareLink} />
              <button
                onClick={whatsapp}
                title="Send via WhatsApp"
                className="flex shrink-0 items-center gap-1.5 rounded-lg px-3 text-[12px] font-semibold text-white"
                style={{ background: "#25D366" }}
              >
                <MessageCircle size={13} /> WhatsApp
              </button>
            </div>

            {/* PDF / Email / QR */}
            <div className="mt-2 flex gap-2">
              <ActionBtn icon={<FileText size={13} />} onClick={() => toast({ tone: "info", title: "PDF lookbook — coming soon" })}>
                PDF lookbook
              </ActionBtn>
              <ActionBtn icon={<Mail size={13} />} onClick={emailShare}>
                Email
              </ActionBtn>
              <ActionBtn icon={<QrCode size={13} />} active={qrOpen} onClick={() => setQrOpen((v) => !v)}>
                QR for showroom
              </ActionBtn>
            </div>
            {qrOpen && (
              <div className="mt-2.5 flex flex-col items-center gap-1.5 rounded-xl border border-white/50 bg-white/30 p-4">
                {qrUrl ? (
                  <img src={qrUrl} alt="QR code for share link" className="h-40 w-40 rounded-lg bg-white p-1.5" />
                ) : (
                  <div className="grid h-40 w-40 place-items-center"><Loader /></div>
                )}
                <p className="text-[11px] text-ink-400">Scan to open on any device</p>
              </div>
            )}
            <p className="mt-2 text-[11px] text-ink-400">{embed.viewCount} views</p>
          </div>

          {/* Embed code + branding + domain allow-list (real) */}
          <div className="space-y-3">
            <CopyRow label="Embed code" value={buildSnippet(base, embed.token)} multiline />
            <button
              onClick={() => setEmbedOpen(true)}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/60 py-2 text-[12.5px] font-medium text-ink-700 transition-colors hover:border-gold-300"
            >
              <Settings2 size={14} /> Customize viewer & branding
            </button>
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                {domains.length ? <Lock size={12} /> : <Globe size={12} />}
                Allowed domains
              </p>
              <div className="flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDomain())}
                  placeholder="shop.yourbrand.com"
                  className="flex-1 rounded-lg border border-white/60 bg-white/40 px-3 py-2 text-[13px] text-ink-900 outline-none focus:border-gold-400"
                />
                <button onClick={addDomain} className="flex items-center gap-1 rounded-lg border border-white/60 px-3 text-[12.5px] font-medium text-ink-700 hover:border-gold-300">
                  <Plus size={14} /> Add
                </button>
              </div>
              {domains.length > 0 ? (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {domains.map((d) => (
                    <span key={d} className="flex items-center gap-1.5 rounded-full border border-white/50 bg-white/40 py-1 pl-2.5 pr-1.5 text-[12px] text-ink-700">
                      {d}
                      <button onClick={() => removeDomain(d)} className="rounded-full p-0.5 text-ink-400 hover:bg-ink-200 hover:text-ink-800">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 flex items-center gap-1.5 text-[11.5px] text-gold-700">
                  <ShieldAlert size={13} /> Embeddable on <strong>any</strong> domain. Add domains to lock it down.
                </p>
              )}
              {dirty && (
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="secondary" disabled={saving} onClick={saveDomains}>
                    {saving ? "Saving…" : "Save domains"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Access toggles */}
          <div className="divide-y divide-[rgba(112,94,64,.12)] rounded-xl border border-white/50 bg-white/20 px-3.5">
            <ToggleRow
              icon={<Eye size={15} className="text-gold-500" />}
              title="Make file public"
              hint="Anyone with the link can view."
              right={<Switch checked={published} disabled={pubBusy} onChange={togglePublish} />}
            />
            <ToggleRow
              icon={<ShieldCheck size={15} className="text-gold-500" />}
              title="Access level"
              hint="What anyone with the link can do."
              right={<MiniSelect value={accessRole} onChange={changeAccess} options={ROLE_OPTIONS} className="w-[130px]" />}
            />
            <ToggleRow
              icon={<Clock size={15} className="text-gold-500" />}
              title="Expiry"
              hint="Link stops working after this date."
              right={<Switch checked={expiryOn} onChange={toggleExpiry} />}
            >
              {expiryOn && (
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => changeExpiryDate(e.target.value)}
                  className="mt-2 h-9 rounded-lg border border-white/60 bg-white/40 px-2.5 text-[12.5px] text-ink-800 outline-none focus:border-gold-400"
                />
              )}
            </ToggleRow>
            <ToggleRow
              icon={<KeyRound size={15} className="text-gold-500" />}
              title="Password protect"
              hint="Visitors must enter a password."
              right={<Switch checked={password} onChange={() => setPassword((v) => !v)} />}
            />
            <ToggleRow
              icon={<Stamp size={15} className="text-gold-500" />}
              title="Watermark previews"
              hint="Your logo over every render until they download."
              right={<Switch checked={watermark} onChange={() => setWatermark((v) => !v)} />}
            />
            <ToggleRow
              icon={<Download size={15} className="text-gold-500" />}
              title="Allow 3D model download"
              hint="Off = clients see renders & turntables only, never the CAD."
              right={<Switch checked={allowDownload} disabled={dlBusy} onChange={toggleDownload} />}
            />
            <ToggleRow
              icon={<Bell size={15} className="text-gold-500" />}
              title="Notify me on open"
              hint="Email + in-app ping when the client views."
              right={<Switch checked={notify} onChange={() => setNotify((v) => !v)} />}
            />
          </div>

          {/* Footer */}
          <div className="flex gap-2 border-t border-ink-100 pt-4">
            <Button variant="ghost" fullWidth leadingIcon={<Eye size={14} />} onClick={previewAsClient}>
              Preview as client
            </Button>
            <Button variant="primary" fullWidth onClick={done}>
              Done
            </Button>
          </div>
        </div>
      )}

      {embedOpen && <EmbedSettings model={model} onClose={() => setEmbedOpen(false)} />}
    </Modal>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">{children}</p>;
}

function PersonRow({ name, email, avatar, role }: { name: string; email: string; avatar?: string; role: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <Avatar src={avatar} initials={name.slice(0, 1).toUpperCase()} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-ink-900">{name}</p>
        {email && <p className="truncate text-[11.5px] text-ink-400">{email}</p>}
      </div>
      <span className="text-[11.5px] font-medium text-ink-500">{role}</span>
    </div>
  );
}

function ToggleRow({
  icon,
  title,
  hint,
  right,
  children,
}: {
  icon: ReactNode;
  title: string;
  hint: string;
  right: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5">{icon}</span>
          <div>
            <p className="text-[13px] font-semibold text-ink-900">{title}</p>
            <p className="text-[11.5px] text-ink-400">{hint}</p>
          </div>
        </div>
        <div className="shrink-0">{right}</div>
      </div>
      {children}
    </div>
  );
}

function MiniSelect({
  value,
  onChange,
  options,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`h-9 rounded-lg border border-white/60 bg-white/40 px-2.5 text-[12.5px] text-ink-800 outline-none focus:border-gold-400 ${className}`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function ActionBtn({ icon, active, onClick, children }: { icon: ReactNode; active?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-2 text-[12px] font-medium transition-colors ${
        active ? "border-gold-400 bg-gold-50 text-gold-700" : "border-white/60 bg-white/30 text-ink-700 hover:border-gold-300"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function CopyBtn({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-ink-900 px-3 text-[12px] font-semibold text-white hover:bg-ink-800"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function CopyRow({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-400">{label}</p>
      <div className="flex items-stretch gap-2">
        <code className={`flex-1 rounded-lg border border-white/60 bg-white/40 px-3 py-2 font-mono text-[11.5px] text-ink-700 ${multiline ? "whitespace-pre-wrap break-all" : "truncate"}`}>
          {value}
        </code>
        <CopyBtn value={value} />
      </div>
    </div>
  );
}
